import React, { FC } from "react";
import { Link } from "wouter";
import { twMerge } from "tailwind-merge";
import { useStore } from "../../store/store";
import { filterHoverClasses } from "../../utils/css";

interface SlidingIconButtonProps {
  to?: string;
  color?: string;
  text: string;
  iconClass: string;
  className?: string;
  onClick?: () => void;
  openByDefault?: boolean;
  reverse?: boolean;
  reverseAnimation?: boolean;
  disabled?: boolean;
}

const SlidingIconButton: FC<SlidingIconButtonProps> = ({
  to,
  text,
  iconClass,
  className = "",
  onClick,
  openByDefault = false,
  reverse = false,
  reverseAnimation = false,
  disabled = false,
}) => {
  const classes = [
    "button group relative flex items-center w-[2.4rem] h-[2.4rem] min-w-[2.4rem] min-h-[2.4rem] p-1 border border-2 border-white/50",
    "rounded-full bg-transparent backdrop-blur text-[0.9rem] outline-none whitespace-nowrap overflow-hidden transition-all duration-500 ease-in-out",
  ];

  const { audioManager } = useStore();

  // Add width classes based on openByDefault and disabled
  if (openByDefault) {
    classes.push("w-[8.4rem]");
  } else if (!disabled) {
    // Only add hover class if not disabled
    classes.push("hover:w-[8.4rem]");
  }

  // Determine justify content based on reverseAnimation and reverse flags
  const justifyClass = reverseAnimation
    ? "justify-start"
    : reverse
      ? "justify-end"
      : "justify-start";

  // Add disabled styles if disabled
  if (disabled) {
    classes.push("cursor-not-allowed opacity-50");
  } else {
    classes.push("cursor-pointer");
  }

  // Filter out hover classes from className if disabled
  const processedClassName = disabled
    ? filterHoverClasses(className)
    : className;

  const baseClasses = twMerge(
    classes.join(" "),
    justifyClass,
    processedClassName,
  );

  // Icon positioning based on reverse and reverseAnimation flags
  const iconPositionClasses = reverseAnimation
    ? "left-[0.6rem]"
    : reverse
      ? "right-[0.6rem] left-auto"
      : "left-[0.6rem]";

  // Icon styles
  const iconClasses = twMerge(
    "absolute flex items-center h-full",
    iconPositionClasses,
    "d-icon text-base",
  );

  // Text positioning based on reverse and reverseAnimation flags
  const textPaddingClass = reverseAnimation
    ? "pl-7"
    : reverse
      ? "pr-7"
      : "pl-7";

  // Expanding text styles
  const expandingTextClasses = twMerge(
    "absolute flex items-center",
    textPaddingClass,
    "transition-opacity duration-500",
    "whitespace-nowrap",
    "pointer-events-none",
    openByDefault ? "" : "opacity-0 group-hover:opacity-100",
  );

  // If disabled, ensure no hover opacity classes are applied
  const finalExpandingTextClasses = disabled
    ? twMerge(expandingTextClasses, "group-hover:opacity-0 opacity-0")
    : expandingTextClasses;

  // Click event handler
  const handleClick = (event: React.MouseEvent) => {
    if (disabled) {
      event.preventDefault();
      return;
    }
    audioManager?.play("click");
    if (onClick) onClick();
  };

  // Common content
  const content = (
    <>
      <span className={`${iconClasses} ${iconClass}`}></span>
      <span className={finalExpandingTextClasses}>{text}</span>
    </>
  );

  // Render as Link or button
  if (to) {
    if (disabled) {
      // Render a non-clickable button for disabled state
      return (
        <button
          className={baseClasses}
          disabled
          aria-disabled="true"
          // Optionally, you can add tabIndex="-1" to remove from tab order
          tabIndex={-1}
        >
          {content}
        </button>
      );
    } else {
      return (
        <Link to={to} onClick={handleClick} className={baseClasses}>
          {content}
        </Link>
      );
    }
  }

  return (
    <button
      onClick={handleClick}
      className={baseClasses}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {content}
    </button>
  );
};

export default SlidingIconButton;
